// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Fetcher.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import config from 'config';
import AppGlobal from 'Izenda.AppGlobal';
import uiState from 'logic/Izenda.UiState.js';
import AccountConstants from 'logic/data/Izenda.AccountConstants';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import localizer from 'share/localization/Izenda.Localizer';
import IzendaConfig from 'Izenda.Config';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import { merge, isNil, getOr, isFunction, forEach, mergeAll, some } from 'lodash/fp';

/**
 * Resolve API url from path
 * @param {string} path
 * @returns {string}
 */
function resolveUrl(path: string): string {
  ensure(path || path !== '', 'Path of url must be provided');
  let root = config.WebApiUrl;
  const safePath = path ? (path[0] === '/' ? path.substring(1) : path) : '';
  return root.concat(root.slice(-1) !== '/' ? '/'.concat(safePath) : safePath);
}

/**
 * get timeout for ajax query, then convert to milliseconds by multiply with 1000
 * @returns {number} timeout
 */
function getTimeout(): number {
  return (
    (AppGlobal.advancedSettings.performance.queryTimeoutValue ||
      AppGlobal.advancedSettings.performance.queryTimeoutDefaultValue ||
      config.TimeOut) * 1000
  );
}

/**
 * Build request headers.
 * @param {Object} config
 * @param {Object} securityManager
 * @returns {Object}
 */
function buildHeaders(config: any, securityManager: any): any {
  const headers = {};

  Object.assign(headers, buildAuthHeaders(config, securityManager));

  return headers;
}

/**
 * Build auth request headers.
 * @param {Object} config
 * @param {Object} securityManager
 * @returns {Object}
 */
function buildAuthHeaders(config: any, securityManager: any): any {
  config = config || {};
  if (config.excludeAuthHeader) return null;

  const headers = {};
  const { HEADER_KEYS, HEADER_PREFER } = AccountConstants;

  const accessToken = securityManager.getAccessToken();
  if (!isNil(accessToken)) {
    headers[HEADER_KEYS.access_token] = accessToken;
  }

  const currentTenantValue = uiState.currentUser.tenantId;
  if (!isNil(currentTenantValue)) {
    headers[HEADER_KEYS.current_tenant] = currentTenantValue;
  }

  headers[HEADER_KEYS.selected_tenant] = uiState.tenantId;

  if (!isNil(config.prefer)) {
    headers[HEADER_KEYS.prefer] = HEADER_PREFER[config.prefer];
  }

  return headers;
}

/**
 * Invoke AJAX call to path with associated options
 * @param {string} path
 * @param {Object} options
 * @returns {Promise}
 */
function ajax(path: string, options: any): Promise {
  var final = merge(
    {
      cache: false,
      url: resolveUrl(path),
      contentType: 'application/json',
      timeout: getTimeout(),
      async: !isNil(options.async) ? options.async : true,
      error: function(jqXHR, textStatus) {
        const status = jqXHR.status;
        const message = getOr('', 'responseJSON.message', jqXHR);

        switch (status) {
          case 401:
            const isIframeExported = routeUtils.checkIframeExported();
            if (IzendaConfig.Integration && !isIframeExported) {
              isFunction(IzendaConfig.OnReceiveUnauthorizedResponse) &&
                IzendaConfig.OnReceiveUnauthorizedResponse(message);
            } else {
              uiState.setNeedLogin(true);
            }
            break;
          case 408:
            uiState.setSystemError('Query timeout expired.');
            break;
          case 0:
            uiState.setSystemError(localizer.getText('COMMON_MESSAGES_GENERIC_ERROR'));
            break;
        }

        // Invoke an user-defined error handler
        if(some(statusCode => status === statusCode, [
          401, 500, 501, 502, 503, 504, 
          505, 506, 507, 508, 510, 511
        ]))
          config.ErrorHandler(status, message);
      },
      headers: buildHeaders(options.config, options.securityManager)
    },
    options
  );

  var mainObj = merge(final, IzendaConfig.AjaxSettings);

  return Promise.resolve($izenda.ajax(mainObj));
}

/**
 * API communicator
 */
class Fetcher {
  /**
   * Web API data fetcher
   * @constructor
   * @param {string} path - Query url path
   * @param {Object} config - Advanced configuration.
   */
  constructor(path: string, config: any) {
    this.path = path;
    this.config = config;
    this.securityManager = new SecurityManager();
  }

  /**
   * GET request
   * @returns {Promise}
   */
  get(): Promise {
    return ajax(this.path, {
      type: 'GET',
      config: this.config,
      securityManager: this.securityManager
    });
  }

  /**
   * POST request with payload data
   * @param {Object} payload - POST data
   * @param {Object} cacheKey
   * @param {boolean} async (default is true)
   * @returns {Promise}
   */
  post(payload: any, cacheKey: any, async: boolean = true): Promise {
    return ajax(this.path, {
      type: 'POST',
      data: payload ? JSON.stringify(payload) : payload,
      config: this.config,
      securityManager: this.securityManager,
      async: async
    });
  }

  /**
   * DELETE request with payload data
   * @param {Object} payload - POST data
   * @returns {Promise}
   */
  delete(payload: any): Promise {
    return ajax(this.path, {
      type: 'DELETE',
      data: payload ? JSON.stringify(payload) : payload,
      config: this.config,
      securityManager: this.securityManager
    });
  }

  /**
   * POST request with files
   * @param {Array} files
   * @returns {Promise}
   */
  upload(files: Array<any>): Promise {
    let formData = new FormData();
    forEach(file => {
      formData.append(file.name, file);
    }, files);

    return ajax(this.path, {
      type: 'POST',
      data: formData,
      contentType: false,
      processData: false,
      enctype: 'multipart/form-data',
      config: this.config,
      securityManager: this.securityManager
    });
  }

  /**
   * Get auth header
   * @return {Object}
   */
  getAuthHeader(): any {
    return buildAuthHeaders(this.config, this.securityManager);
  }
}

export default function(path, config) {
  return new Fetcher(path, config);
}



// WEBPACK FOOTER //
// ./ui/share/fetch/Izenda.Fetch.js